import { filterBy } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import Checkbox from 'src/components/design-system/Checkbox';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import useBoolean from 'src/hooks/useBoolean';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useDepartmentCopyApi from 'src/services/useDepartmentCopyFromApi';
import { SelectedDepartment } from './ViewDepartmentContext';

const CopyFrom: FC = () => {
  const {
    t,
    departmentList,
    departments,
    companyId,
    selectedDepartment: editingDepartment,
    refreshTable,
    isManageDepartmentDirty,
    unDeletedDepartmentList,
  } = useViewDepartment();
  const dropdownRef = useRef();
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean();
  const [isGLCodesCopyEnabled, setIsGLCodesCopyEnabled] = useState(true);
  const [isDeletedGLCodesCopyEnabled, setIsDeletedGLCodesCopyEnabled] = useState(false);
  const [isMembersCopyEnabled, setIsMembersCopyEnabled] = useState(true);
  const [isDeletedMembersCopyEnabled, setIsDeletedMembersCopyEnabled] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectedDepartment>(
    {} as SelectedDepartment
  );
  const [data, setData] = useState<SelectedDepartment[]>([]);
  const { copyFromDepartment, isLoading: isSaveLoading } = useDepartmentCopyApi(
    companyId,
    selectedDepartment.departmentId
  );
  const selectableDepartments = useMemo(
    () =>
      departmentList.filter(
        (department) => department.departmentId !== editingDepartment.departmentId
      ),
    [departmentList, editingDepartment.departmentId]
  );

  useEffect(() => {
    setData(selectableDepartments);
  }, [selectableDepartments]);

  const department = useMemo(
    () =>
      departments.find((department) => department.departmentId === selectedDepartment.departmentId),
    [departments, selectedDepartment.departmentId]
  );

  const { undeletedGLCodes, deletedGLCodes, undeletedMembers, deletedMembers } = useMemo(() => {
    const undeletedGLCodes = department?.glCodes?.filter((code) => !code.isDeleted).length;
    const deletedGLCodes = department?.glCodes?.filter((code) => code.isDeleted).length;
    const undeletedMembers = department?.members?.filter((member) => !member.isDeleted).length;
    const deletedMembers = department?.members?.filter((member) => member.isDeleted).length;
    return { undeletedGLCodes, deletedGLCodes, undeletedMembers, deletedMembers };
  }, [department?.glCodes, department?.members]);

  const openCopyFromModal = useCallback(() => {
    setSelectedDepartment(selectableDepartments[0]);
    setIsGLCodesCopyEnabled(true);
    setIsMembersCopyEnabled(true);
    setIsDeletedGLCodesCopyEnabled(false);
    setIsDeletedMembersCopyEnabled(false);
    openModal();
  }, [openModal, selectableDepartments]);

  const filterChange = (event: DropDownListFilterChangeEvent) => {
    setData(filterBy(selectableDepartments, event.filter));
  };

  const handleDepartmentChange = (e: DropDownListChangeEvent) => {
    setSelectedDepartment(e.value);
  };

  const handleSubmitCopy = useCallback(async () => {
    const response = await copyFromDepartment({
      copyGLCodes: isGLCodesCopyEnabled,
      copyMembers: isMembersCopyEnabled,
      copyDeletedGLCodes: isDeletedGLCodesCopyEnabled,
      copyDeletedMembers: isDeletedMembersCopyEnabled,
      destinationDepartmentId: editingDepartment.departmentId,
    });

    if (response.status === 200) {
      closeModal();
      toast.success('Items successfully copied to Department');
      refreshTable?.();
    }
  }, [
    closeModal,
    copyFromDepartment,
    editingDepartment.departmentId,
    isDeletedGLCodesCopyEnabled,
    isDeletedMembersCopyEnabled,
    isGLCodesCopyEnabled,
    isMembersCopyEnabled,
    refreshTable,
  ]);

  const { isConfirmCopyDisable, message } = useMemo(() => {
    const isConfirmCopyDisable = !isGLCodesCopyEnabled && !isMembersCopyEnabled;
    const message = isConfirmCopyDisable ? ['Please select to add GL Codes, Members or both'] : [];

    return { isConfirmCopyDisable, message };
  }, [isGLCodesCopyEnabled, isMembersCopyEnabled]);

  const { copyFromDepartmentMessage, isSingleDepartment } = useMemo(() => {
    const isSingleDepartment = unDeletedDepartmentList.length === 1;
    let copyFromDepartmentMessage: string[] = [];
    if (isSingleDepartment) {
      copyFromDepartmentMessage = [
        'Company should have more than one department to use this feature.',
      ];
    }
    if (isManageDepartmentDirty) {
      copyFromDepartmentMessage = ['Save Department to perform Copy From Action.'];
    }
    return { copyFromDepartmentMessage, isSingleDepartment };
  }, [isManageDepartmentDirty, unDeletedDepartmentList.length]);

  useEffect(() => {
    if (isModalOpen && dropdownRef.current) {
      (dropdownRef.current as any).focus();
    }
  }, [isModalOpen]);

  return (
    <>
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper
              type={isManageDepartmentDirty ? 'Error' : 'Information'}
              contents={copyFromDepartmentMessage}
            />
          </div>
        }
        show={isManageDepartmentDirty || isSingleDepartment}
      >
        <Button
          color="info"
          onClick={openCopyFromModal}
          disabled={isManageDepartmentDirty || isSingleDepartment}
        >
          {t('Add From Other Department')}
        </Button>
      </PopUp>

      <Dialog
        width={400}
        id="modal"
        title={`${t('Add To')} - ${editingDepartment?.departmentName}`}
        open={isModalOpen}
        onClose={closeModal}
        closeIcon={!isSaveLoading}
      >
        Select Department to add from
        <DropDownList
          id="select-department"
          data={data}
          onChange={handleDepartmentChange}
          value={selectedDepartment}
          textField="departmentName"
          dataItemKey="departmentId"
          filterable
          onFilterChange={filterChange}
          ref={dropdownRef}
        />
        <div className="flex items-center pt-3">Include:</div>
        <ul
          className="p-4 m-1 overflow-auto flex flex-col pt-1 ml-0 pl-0"
          style={{ height: 'auto' }}
        >
          <Checkbox
            checked={isGLCodesCopyEnabled}
            onChange={() => {
              if (isGLCodesCopyEnabled) {
                setIsDeletedGLCodesCopyEnabled(false);
              }
              setIsGLCodesCopyEnabled((prev) => !prev);
            }}
            className="mt-1 px-1"
            label={`GL Codes (${undeletedGLCodes})`}
          />

          {isGLCodesCopyEnabled && (
            <Checkbox
              checked={isDeletedGLCodesCopyEnabled}
              onChange={() => setIsDeletedGLCodesCopyEnabled((prev) => !prev)}
              className="mt-1 ml-6"
              label={`Deleted GL Codes (${deletedGLCodes})`}
            />
          )}

          <Checkbox
            checked={isMembersCopyEnabled}
            onChange={() => {
              if (isMembersCopyEnabled) {
                setIsDeletedMembersCopyEnabled(false);
              }
              setIsMembersCopyEnabled((prev) => !prev);
            }}
            className="mt-1 px-1"
            label={`Department Members (${undeletedMembers})`}
          />
          {isMembersCopyEnabled && (
            <Checkbox
              checked={isDeletedMembersCopyEnabled}
              onChange={() => setIsDeletedMembersCopyEnabled((prev) => !prev)}
              className="mt-1 ml-6"
              label={`Disabled Department Members (${deletedMembers})`}
            />
          )}
        </ul>
        <DialogActionsBar>
          <div className="flex flex-row items-center justify-end gap-2">
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper type="Error" contents={message} />
                </div>
              }
              show={isConfirmCopyDisable}
            >
              <Button
                color="info"
                onClick={handleSubmitCopy}
                disabled={isConfirmCopyDisable}
                loading={isSaveLoading}
              >
                Save and Add
              </Button>
            </PopUp>
            <Button color="info" onClick={closeModal} disabled={isSaveLoading}>
              {t('Cancel')}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default CopyFrom;
